import { Server, Key, Shield, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

export const SpfDkimDmarcProtocols = () => {
  const protocols = [
    {
      name: "SPF",
      fullName: "Sender Policy Framework",
      icon: <Server className="w-6 h-6" />,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      description: "SPF defines which mail servers are authorized to send email on behalf of your domain.",
      howItWorks: [
        "You publish a DNS TXT record listing authorized sending IPs",
        "Receiving servers check if the sending IP matches your SPF record",
        "If it doesn't match, the email fails SPF authentication"
      ],
      example: "v=spf1 include:_spf.google.com include:sendgrid.net ~all",
      pros: ["Easy to implement", "Prevents direct spoofing", "Widely supported"],
      cons: ["Breaks when emails are forwarded", "Limit of 10 DNS lookups", "Doesn't verify message content"]
    },
    {
      name: "DKIM",
      fullName: "DomainKeys Identified Mail",
      icon: <Key className="w-6 h-6" />,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      description: "DKIM adds a digital signature to your emails, proving they haven't been tampered with.",
      howItWorks: [
        "Your mail server signs outgoing emails with a private key",
        "The public key is published in your DNS records",
        "Receiving servers verify the signature matches the public key"
      ],
      example: "v=DKIM1; k=rsa; p=MIGfMA0GCSqGSIb3DQEBA...",
      pros: ["Survives forwarding", "Verifies message integrity", "Cryptographically secure"],
      cons: ["More complex to set up", "Key rotation required", "Some ESPs don't support custom DKIM"]
    },
    {
      name: "DMARC",
      fullName: "Domain-based Message Authentication",
      icon: <Shield className="w-6 h-6" />,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      description: "DMARC ties SPF and DKIM together and tells receivers what to do when authentication fails.",
      howItWorks: [
        "Checks if SPF or DKIM pass AND align with the From: domain",
        "Applies your policy: none (monitor), quarantine, or reject",
        "Sends aggregate reports to help you monitor authentication"
      ],
      example: "v=DMARC1; p=quarantine; rua=mailto:dmarc@yourdomain.com; pct=100",
      pros: ["Unified authentication", "Reporting capabilities", "Brand protection"],
      cons: ["Requires SPF and DKIM first", "Can break legitimate email if misconfigured", "Reports need analysis"]
    }
  ];

  return (
    <section className="py-10 sm:py-12">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-8 text-center">
        The Three Protocols Explained
      </h2>
      
      <div className="space-y-6">
        {protocols.map((protocol, index) => (
          <div key={index} className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className={`${protocol.bgColor} p-6 border-b border-border`}>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-background flex items-center justify-center ${protocol.color}`}>
                  {protocol.icon}
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-foreground">{protocol.name}</h3>
                  <p className="text-sm text-muted-foreground">{protocol.fullName}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <p className="text-muted-foreground">{protocol.description}</p>
              
              <div>
                <h4 className="font-semibold text-foreground mb-3">How it works:</h4>
                <ol className="space-y-2">
                  {protocol.howItWorks.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              
              <div>
                <h4 className="font-semibold text-foreground mb-2">Example record:</h4>
                <code className="block bg-muted/50 rounded-lg p-3 text-sm text-foreground overflow-x-auto">
                  {protocol.example}
                </code>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-success" />
                    Pros
                  </h4>
                  <ul className="space-y-1.5">
                    {protocol.pros.map((pro, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-success" />
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-warning" />
                    Considerations
                  </h4>
                  <ul className="space-y-1.5">
                    {protocol.cons.map((con, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning" />
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
